/**
 * Expenses advanced-filter document — a versioned value the web filter panel
 * (`ExpenseFilterPanel`) edits and evaluates CLIENT-SIDE over the complete
 * expense index. One schema + ONE evaluator, both in @releve/shared (the
 * places/Impact template, and the Accounting twins `apBillFilters.ts` /
 * `arInvoiceFilters.ts`), shared by:
 *  - the web filter panel (live evaluation as controls change),
 *  - any future server consumer (reuse `applyExpenseFilterDocument` — never
 *    build SQL WHERE clauses from the document).
 *
 * The document is AD HOC — it lives in component state for the session.
 *
 * ── THIS WAS A MOVE, NOT AN EXPANSION ───────────────────────────────────────
 * v1's vocabulary started as EXACTLY the eight columns the grid used to mark
 * `filterable: true` (expenseGridColumns.tsx), so the conversion to the panel
 * was behaviour-preserving and reviewable. Amount ranges are deliberately
 * absent: net-new UI, not a port. The one addition since is the DERIVED
 * `expense.bank_match` field (the work-queue "Not matched to bank" chip —
 * precedent `bill.payment_status` in apBillFilters.ts).
 *
 * Per docs/INDEX_GRID_PATTERN.md step 9b, a grid runs the panel OR filterable
 * columns, never both. The expenses grid ends with ZERO filterable columns.
 *
 * v1 fields are `expense.<field>` over the MAPPED grid rows (`ExpenseGridRow`),
 * not the wire row — the payee is already the `b:`/`p:`/raw token and
 * `expense_date` is already a local calendar day, so evaluation runs AFTER the
 * mapper and BEFORE the grid shell (`hasUpstreamFilters`).
 *
 * ── THE PAYEE FIELD CARRIES RAW NAMES ───────────────────────────────────────
 * Unlike AP (payee XOR is a table CHECK), an expense may name a free-text
 * vendor: its payee token IS the raw `custom_business_name`. That name has no
 * length bound in the database, while a condition value is capped at 200
 * characters and 50 values (`filterDocumentPrimitives.ts`). The panel never
 * parses its ad-hoc document, so filtering on such a vendor still works — but
 * a document built from it will NOT validate against the schema. Any future
 * consumer that persists or parses the document must handle that.
 */
import { z } from "zod";
import { makeDateConditionSchema, makeFilterDocumentSchema, makeStringConditionSchema, matchesDateOp, matchesStringOp, toLocalCalendarDay, } from "./filterDocumentPrimitives.js";
// ---------------------------------------------------------------------------
// Field vocabulary (v1) — the eight ex-`filterable` columns + bank_match
// ---------------------------------------------------------------------------
export const EXPENSE_FILTER_STRING_FIELDS = [
    "expense.payee_id",
    "expense.tax_summary",
    "expense.wkr_summary",
    "expense.payment_account_id",
    "expense.project_id",
    "expense.fund_id",
    "expense.bank_match",
];
export const EXPENSE_FILTER_DATE_FIELDS = [
    "expense.expense_date",
    "expense.record_created_at",
];
// ---------------------------------------------------------------------------
// Conditions
// ---------------------------------------------------------------------------
const stringConditionSchema = makeStringConditionSchema(EXPENSE_FILTER_STRING_FIELDS);
const dateConditionSchema = makeDateConditionSchema(EXPENSE_FILTER_DATE_FIELDS);
export const expenseFilterConditionSchema = z.union([
    stringConditionSchema,
    dateConditionSchema,
]);
// ---------------------------------------------------------------------------
// The document
// ---------------------------------------------------------------------------
export const expenseFilterDocumentSchema = makeFilterDocumentSchema(expenseFilterConditionSchema);
/** The empty document — "no filters active". */
export const EMPTY_EXPENSE_FILTER_DOCUMENT = {
    version: 1,
    match: "all",
    conditions: [],
};
/*
 * One accessor per field FAMILY, each an exhaustive switch over its
 * vocabulary: a new vocabulary entry is a `TS2366: Function lacks ending
 * return statement` until it is handled here, instead of compiling clean and
 * filtering the wrong column.
 */
function stringValue(row, field) {
    switch (field) {
        case "expense.payee_id":
            return row.payee_id ?? "";
        case "expense.tax_summary":
            // Already `''` (never null) on an expense with no lines.
            return row.tax_summary;
        case "expense.wkr_summary":
            return row.wkr_summary ?? "";
        case "expense.payment_account_id":
            return row.payment_account_id ?? "";
        case "expense.project_id":
            return row.project_id ?? "";
        case "expense.fund_id":
            return row.fund_id ?? "";
        case "expense.bank_match":
            // Defensive: a hand-built row may still omit it at runtime.
            return row.bank_match ?? "";
    }
}
/**
 * The LOCAL calendar day (`yyyy-MM-dd`) to compare — verbatim for the
 * mapper-normalized `expense_date`, reduced from the instant for the raw
 * `record_created_at` timestamp. Compared as STRINGS (lexicographic order is
 * date order for this shape).
 */
function dateValue(row, field) {
    switch (field) {
        case "expense.expense_date":
            return row.expense_date;
        case "expense.record_created_at":
            return toLocalCalendarDay(row.record_created_at);
    }
}
// The two condition families have disjoint op sets, so op alone narrows.
function matchesCondition(row, c) {
    switch (c.op) {
        case "in":
        case "is_empty":
            return matchesStringOp(c.op, stringValue(row, c.field), c.values, c.include_empty);
        case "date_gte":
        case "date_lte":
        case "date_between":
            return matchesDateOp(c.op, dateValue(row, c.field), c.min, c.max);
    }
}
/**
 * Apply a filter document to mapped expense rows (AND of all conditions — v1
 * `match` is 'all' only). An empty document returns the input array unchanged
 * (identity — callers can rely on reference equality to skip re-renders).
 */
export function applyExpenseFilterDocument(rows, doc) {
    if (doc.conditions.length === 0)
        return rows;
    return rows.filter((row) => doc.conditions.every((c) => matchesCondition(row, c)));
}
/** Count of active conditions — the filter button's badge. */
export function countActiveExpenseConditions(doc) {
    return doc.conditions.length;
}
//# sourceMappingURL=expenseFilters.js.map